import { useEffect, useMemo, useState } from 'react'

import type { Lot } from '../../types/lot'
import type { LotCostEvent } from '../../types/production'
import { FACILITY_COST_SCOPE, isFacilityCost, lotCostEventApi } from '../../utils/lotCostEventApi'
import { createId } from '../../utils/id'
import { useT } from '../../i18n'
import {
  MANUAL_CATEGORY_PREFIXES,
  applyDefaultsForCategory,
  buildInitialLotCostFormDraft,
  formatCostEventDisplayAmount,
  resolvePreferredCurrency,
  sumCostEventsByCurrency,
  validateLotCostFormDraft,
  type LotCostFormDraft,
  type LotCostFormErrors,
  type ManualCategoryPrefix
} from './lotCostForm'
import {
  groupCostEventsForList,
  parseLotGroupSelection,
  readCostGroupId,
  splitCostByCounts,
  type LotCostGroup
} from './lotCostGroups'

// Kept in sync with lots.costs.form.categoryOption.* i18n keys.
// Only G, N, H, L have i18n labels (manual entry categories + auto-derived N).
// Other prefixes (I/M/A/F/R) fall through to their raw code until UI surfaces
// need them.
type LabelledPrefix = 'G' | 'N' | 'H' | 'L'

type LotCostSectionProps = {
  farmId: string
  productionUnitId?: string
  lots: Lot[]
  canEdit: boolean
  /**
   * @deprecated v2.2 T3-C-07 (LCE-03/06, 2026-07-15): cost logging moved
   * out of the Lots screen and into its own `/costs` surface, so the
   * `<details>`-wrapped embed is no longer used. The prop is retained for
   * one release as a compile-safety net; any consumer that still passes it
   * will render the same standalone `<section>` shape as everyone else.
   * Removal follow-up card lands in the next release.
   */
  collapsible?: boolean
  /**
   * Pre-validation #60: when the host screen already has its own lot
   * selector (`/costs` context bar), it passes the selection here and this
   * section hides its own lot `<select>` — one selector, one value. Omit
   * both to keep the section's internal selector.
   */
  selectedLotId?: string
  onSelectLot?: (lotId: string) => void
  /** Facility default currency (findings #99) — used when the lot has no cost yet. */
  facilityCurrency?: string
  /** Findings #131: lots sown into several units, selectable as "todo el lote". */
  lotGroups?: LotCostGroup[]
  /** Unit names for the split preview and per-unit notes. */
  unitNameById?: ReadonlyMap<string, string>
  /** Findings #131: open the form pre-filled (after "Registrar costo de las ovas"). */
  prefill?: { categoryPrefix?: ManualCategoryPrefix; recordedAt?: string }
}

type EditingCost =
  | { kind: 'single'; event: LotCostEvent }
  | { kind: 'group'; groupId: string; events: LotCostEvent[] }

const draftFromEvent = (event: LotCostEvent, amountCents: number): LotCostFormDraft => ({
  categoryPrefix: event.categoryPrefix as ManualCategoryPrefix,
  amountMajor: (amountCents / 100).toFixed(2),
  currency: event.currency,
  recordedAt: event.recordedAt.slice(0, 10),
  expenseNature: event.expenseNature as LotCostFormDraft['expenseNature'],
  costClassification: event.costClassification as LotCostFormDraft['costClassification'],
  notes: event.notes ?? ''
})

/**
 * Lot selector value for facility-wide costs (findings #100): costs with no
 * lot (electricity, salaries…), summed and never allocated to lots.
 */
export const FACILITY_COST_SELECTION = '__facility__'

const cardStyle: React.CSSProperties = {
  border: '1px solid #e2e8f0',
  borderRadius: 12,
  padding: 16,
  backgroundColor: '#ffffff',
  display: 'grid',
  gap: 12
}

const fieldGroupStyle: React.CSSProperties = {
  display: 'grid',
  gap: 4
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  fontSize: 14
}

const errorInputStyle: React.CSSProperties = { ...inputStyle, borderColor: '#ef4444' }

const primaryButtonStyle: React.CSSProperties = {
  padding: '8px 14px',
  border: 'none',
  borderRadius: 8,
  backgroundColor: '#0f172a',
  color: '#ffffff',
  fontWeight: 600,
  cursor: 'pointer'
}

const secondaryButtonStyle: React.CSSProperties = {
  padding: '8px 14px',
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  backgroundColor: '#ffffff',
  color: '#1e293b',
  fontWeight: 600,
  cursor: 'pointer'
}

const dangerButtonStyle: React.CSSProperties = {
  padding: '4px 10px',
  border: '1px solid #fca5a5',
  borderRadius: 6,
  backgroundColor: '#fef2f2',
  color: '#b91c1c',
  fontSize: 12,
  cursor: 'pointer'
}

const editButtonStyle: React.CSSProperties = {
  padding: '4px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: 6,
  backgroundColor: '#ffffff',
  color: '#1e293b',
  fontSize: 12,
  cursor: 'pointer'
}

const toggleGroupStyle: React.CSSProperties = {
  display: 'flex',
  gap: 4,
  border: '1px solid #cbd5e1',
  borderRadius: 8,
  padding: 2,
  width: 'fit-content'
}

const toggleButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: '4px 12px',
  border: 'none',
  borderRadius: 6,
  backgroundColor: active ? '#0f172a' : 'transparent',
  color: active ? '#ffffff' : '#475569',
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer'
})

/**
 * Manual cost-event entry section for the Lots screen (T3-B-03c).
 * Lets operators log Genetics / Health / Labor costs against a selected lot.
 * Feed (N) costs are auto-derived at the feedLogApi boundary (T3-B-03b) and
 * NOT manually enterable here.
 */
export const LotCostSection = ({
  farmId,
  productionUnitId,
  lots,
  canEdit,
  selectedLotId: controlledLotId,
  onSelectLot,
  facilityCurrency,
  lotGroups = [],
  unitNameById,
  prefill
}: LotCostSectionProps) => {
  const t = useT()

  const scopedLots = useMemo(() => {
    return lots
      .filter((lot) => lot.farmId === farmId)
      .filter((lot) => !productionUnitId || lot.productionUnitId === productionUnitId)
      .sort((a, b) => (a.lotKey || '').localeCompare(b.lotKey || ''))
  }, [farmId, productionUnitId, lots])

  const isLotControlled = onSelectLot !== undefined
  const [internalLotId, setInternalLotId] = useState<string>('')
  const selectedLotId = isLotControlled ? (controlledLotId ?? '') : internalLotId
  const setSelectedLotId = isLotControlled ? onSelectLot : setInternalLotId
  const [events, setEvents] = useState<LotCostEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [formOpen, setFormOpen] = useState(false)
  const [draft, setDraft] = useState<LotCostFormDraft>(buildInitialLotCostFormDraft)
  const [errors, setErrors] = useState<LotCostFormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  // v2.2 T3-B-05b: per-form-open dismissal of the currency-mismatch
  // banner. State-only, no persistence — reopening the form or reset-
  // ting after a submit clears the dismissal.
  const [currencyBannerDismissed, setCurrencyBannerDismissed] = useState(false)
  const [editing, setEditing] = useState<EditingCost | null>(null)

  const isFacilityScope = selectedLotId === FACILITY_COST_SELECTION
  const selectedGroupRootId = parseLotGroupSelection(selectedLotId)
  const selectedGroup = useMemo(
    () => (selectedGroupRootId ? lotGroups.find((group) => group.rootId === selectedGroupRootId) ?? null : null),
    [lotGroups, selectedGroupRootId]
  )
  const groupMemberKey = selectedGroup ? selectedGroup.members.map((member) => member.lotId).join('|') : ''
  const unitName = (unitId: string | undefined | null): string => (unitId ? unitNameById?.get(unitId) ?? unitId : '')

  // Reset selected lot when the scoped list changes (e.g. facility filter switch).
  useEffect(() => {
    if (selectedLotId === FACILITY_COST_SELECTION) return
    if (parseLotGroupSelection(selectedLotId)) return
    if (scopedLots.length === 0) {
      setSelectedLotId('')
      return
    }
    if (!scopedLots.some((lot) => lot.id === selectedLotId)) {
      setSelectedLotId(scopedLots[0].id)
    }
  }, [scopedLots, selectedLotId])

  // Load events whenever the selected lot changes.
  useEffect(() => {
    if (!selectedLotId) {
      setEvents([])
      return
    }
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    const groupLotIds = groupMemberKey ? groupMemberKey.split('|') : []
    const request = selectedLotId === FACILITY_COST_SELECTION
      ? lotCostEventApi.list({ farmId }).then((result) => result.filter(isFacilityCost))
      : parseLotGroupSelection(selectedLotId)
        ? Promise.all(groupLotIds.map((lotId) => lotCostEventApi.list({ lotId })))
          .then((lists) => lists.flat().sort((a, b) => b.recordedAt.localeCompare(a.recordedAt)))
        : lotCostEventApi.list({ lotId: selectedLotId })
    void request
      .then((result) => {
        if (cancelled) return
        setEvents(result)
      })
      .catch((error) => {
        if (cancelled) return
        setEvents([])
        setLoadError(error instanceof Error ? error.message : t('lots.costs.loadError'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedLotId, farmId, groupMemberKey, t])

  const selectedLot = useMemo(
    () => scopedLots.find((lot) => lot.id === selectedLotId),
    [scopedLots, selectedLotId]
  )
  const hasCostTarget = Boolean(selectedLot) || isFacilityScope || Boolean(selectedGroup)

  // T3-B-03d: totals summary — sums cost events for the selected lot,
  // grouping by currency (a lot could in principle have mixed-currency events;
  // display each total independently rather than pretending an FX conversion).
  const totalsByCurrency = useMemo(() => sumCostEventsByCurrency(events), [events])

  // v2.2 T3-B-05b: sorted list of currency codes already present on this
  // lot's cost events. Used to detect operator-typo currency mismatches
  // at entry time (soft warning banner). Falls out of `totalsByCurrency`
  // so we don't re-scan `events` a second time.
  const existingCurrencies = useMemo(
    () => totalsByCurrency.map(([currency]) => currency),
    [totalsByCurrency]
  )
  const draftCurrencyCode = draft.currency.trim().toUpperCase()
  const showCurrencyMismatchBanner = (
    !currencyBannerDismissed
    && draftCurrencyCode.length === 3
    && existingCurrencies.length > 0
    && !existingCurrencies.includes(draftCurrencyCode)
  )

  const resetForm = () => {
    // v2.2 T3-B-05b: default the currency dropdown to the most-recent
    // currency on this lot's existing events (falls back to USD when
    // no history is present). Prevents 90% of MIXED-currency operator
    // errors at entry time on farms with a single working currency.
    setDraft(buildInitialLotCostFormDraft(resolvePreferredCurrency(events) ?? facilityCurrency))
    setErrors({})
    setCurrencyBannerDismissed(false)
  }

  const openForm = () => {
    resetForm()
    setEditing(null)
    setFormOpen(true)
    setNotice(null)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
    resetForm()
  }

  // Findings #131: Editar — a single cost, or a whole-lot cost (its total).
  const openEdit = (target: EditingCost) => {
    const first = target.kind === 'single' ? target.event : target.events[0]
    const total = target.kind === 'single' ? target.event.amountCents : target.events.reduce((sum, event) => sum + event.amountCents, 0)
    setDraft(draftFromEvent(first, total))
    setErrors({})
    setCurrencyBannerDismissed(true)
    setEditing(target)
    setFormOpen(true)
    setNotice(null)
  }

  // "Registrar costo de las ovas": open the form once with the given defaults.
  const [prefillApplied, setPrefillApplied] = useState(false)
  useEffect(() => {
    if (!prefill || prefillApplied || !canEdit || !hasCostTarget || loading) return
    setPrefillApplied(true)
    const base = applyDefaultsForCategory(
      buildInitialLotCostFormDraft(resolvePreferredCurrency(events) ?? facilityCurrency),
      prefill.categoryPrefix ?? 'G'
    )
    setDraft(prefill.recordedAt ? { ...base, recordedAt: prefill.recordedAt.slice(0, 10) } : base)
    setErrors({})
    setEditing(null)
    setFormOpen(true)
  }, [prefill, prefillApplied, canEdit, hasCostTarget, loading, events, facilityCurrency])

  // Split preview for "todo el lote" (new cost or edit of a whole-lot cost).
  const groupShares = useMemo(() => {
    if (!selectedGroup || (editing && editing.kind === 'single')) return []
    const amount = Number(draft.amountMajor.replace(',', '.'))
    const cents = Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0
    const members = editing?.kind === 'group'
      ? selectedGroup.members.filter((member) => editing.events.some((event) => event.lotId === member.lotId))
      : selectedGroup.members
    return splitCostByCounts(cents, members)
  }, [selectedGroup, editing, draft.amountMajor])

  const setCategory = (prefix: ManualCategoryPrefix) => {
    setDraft((prev) => applyDefaultsForCategory(prev, prefix))
  }

  const handleSubmit = async () => {
    if (!canEdit || !hasCostTarget) return
    setNotice(null)

    const validation = validateLotCostFormDraft(draft)
    if (!validation.ok) {
      setErrors(validation.errors)
      return
    }
    setErrors({})
    setSubmitting(true)
    try {
      if (editing) {
        await saveEdit(editing, validation.payload)
        return
      }
      if (selectedGroup) {
        await saveGroupCost(validation.payload)
        return
      }
      const result = await lotCostEventApi.create(selectedLot
        ? {
            ...validation.payload,
            farmId: selectedLot.farmId,
            lotId: selectedLot.id,
            productionUnitId: selectedLot.productionUnitId
          }
        : {
            ...validation.payload,
            farmId,
            lotId: '',
            metadata: { scope: FACILITY_COST_SCOPE }
          })
      setEvents((prev) => [result.data, ...prev])
      closeForm()
      setNotice({ kind: 'success', text: t('lots.costs.saved') })

      // v2.1 finding #84: LotCostEvent's authoritative accounting fields
      // (`categoryPrefix` / `categorySequence` / `categoryCode`, e.g. `G001`)
      // are server-authored. The optimistic Dexie shape carries a `PENDING`
      // placeholder; without this hydration the row would display `pending`
      // in the list until the operator forced a refresh. After the outbox
      // drain resolves, `syncUpsertLotCostEvent` upserts the server response
      // into Dexie, so re-fetching by id backfills the placeholder in place.
      if (result.syncPromise) {
        void result.syncPromise.then(async (syncState) => {
          if (syncState !== 'synced') return
          try {
            const hydrated = await lotCostEventApi.get(result.data.id)
            if (!hydrated) return
            setEvents((prev) => prev.map((entry) => (
              entry.id === hydrated.id ? hydrated : entry
            )))
          } catch {
            // Best-effort hydration; operator can still hit Refresh manually.
          }
        })
      }
    } catch (error) {
      setNotice({
        kind: 'error',
        text: error instanceof Error ? error.message : t('lots.costs.saveError')
      })
    } finally {
      setSubmitting(false)
    }
  }

  type ValidPayload = Extract<ReturnType<typeof validateLotCostFormDraft>, { ok: true }>['payload']

  // Findings #131: one cost for the whole lot -> one cost per unit, split by
  // the eggs / fish each unit received, linked by metadata.costGroupId.
  const saveGroupCost = async (payload: ValidPayload) => {
    if (!selectedGroup) return
    const groupId = createId('costgroup')
    const totalCents = Number(payload.amountCents)
    const shares = splitCostByCounts(totalCents, selectedGroup.members)
    const created: LotCostEvent[] = []
    for (const share of shares) {
      const result = await lotCostEventApi.create({
        ...payload,
        amountCents: share.amountCents,
        farmId,
        lotId: share.lotId,
        productionUnitId: share.productionUnitId,
        metadata: {
          costGroupId: groupId,
          costGroupTotalCents: totalCents,
          costGroupUnits: shares.length,
          costGroupCount: share.count
        }
      })
      created.push(result.data)
    }
    setEvents((prev) => [...created, ...prev])
    closeForm()
    setNotice({ kind: 'success', text: t('lots.costs.group.saved', { count: shares.length }) })
  }

  const saveEdit = async (target: EditingCost, payload: ValidPayload) => {
    const shared = {
      recordedAt: payload.recordedAt,
      currency: payload.currency,
      expenseNature: payload.expenseNature,
      costClassification: payload.costClassification,
      notes: payload.notes ?? null
    }
    if (target.kind === 'single') {
      const result = await lotCostEventApi.update(target.event.id, { ...shared, amountCents: payload.amountCents })
      setEvents((prev) => prev.map((entry) => (entry.id === target.event.id ? result.data : entry)))
    } else {
      const members = target.events.map((event) => ({
        lotId: event.lotId ?? '',
        productionUnitId: event.productionUnitId ?? '',
        count: selectedGroup?.members.find((member) => member.lotId === event.lotId)?.count ?? null
      }))
      const shares = splitCostByCounts(Number(payload.amountCents), members)
      const updated = new Map<string, LotCostEvent>()
      for (const [index, event] of target.events.entries()) {
        const result = await lotCostEventApi.update(event.id, {
          ...shared,
          amountCents: shares[index].amountCents,
          metadata: { ...(event.metadata ?? {}), costGroupTotalCents: Number(payload.amountCents) }
        })
        updated.set(event.id, result.data)
      }
      setEvents((prev) => prev.map((entry) => updated.get(entry.id) ?? entry))
    }
    closeForm()
    setNotice({ kind: 'success', text: t('lots.costs.edit.saved') })
  }

  const handleDeleteGroup = async (groupEvents: LotCostEvent[]) => {
    if (!canEdit) return
    setNotice(null)
    try {
      for (const event of groupEvents) await lotCostEventApi.delete(event.id)
      const ids = new Set(groupEvents.map((event) => event.id))
      setEvents((prev) => prev.filter((entry) => !ids.has(entry.id)))
      setPendingDeleteId(null)
      setNotice({ kind: 'success', text: t('lots.costs.deleted') })
    } catch (error) {
      setNotice({ kind: 'error', text: error instanceof Error ? error.message : t('lots.costs.deleteError') })
    }
  }

  const handleDelete = async (eventId: string) => {
    if (!canEdit) return
    setNotice(null)
    try {
      await lotCostEventApi.delete(eventId)
      setEvents((prev) => prev.filter((entry) => entry.id !== eventId))
      setPendingDeleteId(null)
      setNotice({ kind: 'success', text: t('lots.costs.deleted') })
    } catch (error) {
      setNotice({
        kind: 'error',
        text: error instanceof Error ? error.message : t('lots.costs.deleteError')
      })
    }
  }

  const renderCategoryLabel = (prefix: string): string => {
    switch (prefix as LabelledPrefix) {
      case 'G':
        return t('lots.costs.form.categoryOption.G')
      case 'N':
        return t('lots.costs.form.categoryOption.N')
      case 'H':
        return t('lots.costs.form.categoryOption.H')
      case 'L':
        return t('lots.costs.form.categoryOption.L')
      default:
        return prefix
    }
  }

  const renderNatureLabel = (nature: string): string =>
    nature === 'fixed' ? t('lots.costs.form.natureOption.fixed') : t('lots.costs.form.natureOption.variable')

  const renderClassificationLabel = (classification: string): string =>
    classification === 'direct'
      ? t('lots.costs.form.classificationOption.direct')
      : t('lots.costs.form.classificationOption.indirect')

  // v2.2 T3-C-07 (LCE-03, 2026-07-15): after the cost extraction the outer
  // container is always a `<section>`; the previous `<details>`/`<summary>`
  // wrapper (T3-C-05 follow-up) is retired along with the collapsible prop.
  // The Add-button therefore no longer needs `preventDefault`/`stopPropagation`
  // to defeat a `<summary>` toggle.
  return (
    <section data-testid="lots-cost-section" style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <strong style={{ fontSize: 15, color: '#0f172a' }}>{t('lots.costs.title')}</strong>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{t('lots.costs.subtitle')}</div>
        </div>
        {canEdit && hasCostTarget && !formOpen ? (
          <button
            type="button"
            onClick={openForm}
            style={primaryButtonStyle}
            data-testid="lots-cost-add"
          >
            {t('lots.costs.addButton')}
          </button>
        ) : null}
      </div>

      {isLotControlled ? null : (
      <div style={fieldGroupStyle}>
        <label htmlFor="lots-cost-lot-select" style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
          {t('lots.costs.lotSelect')}
        </label>
        <select
          id="lots-cost-lot-select"
          value={selectedLotId}
          onChange={(event) => setSelectedLotId(event.target.value)}
          disabled={scopedLots.length === 0}
          style={inputStyle}
        >
          {scopedLots.length === 0 ? (
            <option value="">{t('lots.costs.noLotsInScope')}</option>
          ) : (
            scopedLots.map((lot) => (
              <option key={lot.id} value={lot.id}>
                {lot.lotKey}
              </option>
            ))
          )}
        </select>
      </div>
      )}

      {notice ? (
        <div
          role="status"
          style={{
            padding: '8px 12px',
            borderRadius: 8,
            fontSize: 13,
            backgroundColor: notice.kind === 'success' ? '#ecfdf5' : '#fef2f2',
            color: notice.kind === 'success' ? '#065f46' : '#991b1b'
          }}
        >
          {notice.text}
        </div>
      ) : null}

      {hasCostTarget && totalsByCurrency.length > 0 ? (
        <div
          data-testid="lots-cost-total"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            borderRadius: 8,
            backgroundColor: '#f1f5f9',
            fontSize: 13,
            color: '#0f172a',
            gap: 8,
            flexWrap: 'wrap'
          }}
        >
          <strong>{isFacilityScope ? t('costs.facilityWide.totalLabel') : selectedGroup ? t('lots.costs.group.totalLabel', { count: selectedGroup.members.length }) : t('lots.costs.totalLabel')}</strong>
          <span style={{ fontWeight: 700 }}>
            {totalsByCurrency
              .map(([currency, cents]) => formatCostEventDisplayAmount(cents, currency))
              .join(' · ')}
          </span>
        </div>
      ) : null}

      {formOpen && hasCostTarget ? (
        <div
          data-testid="lots-cost-form"
          style={{ display: 'grid', gap: 10, padding: 12, backgroundColor: '#f8fafc', borderRadius: 8 }}
        >
          {editing || selectedGroup ? (
            <div data-testid="lots-cost-form-heading" style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              {editing
                ? (editing.kind === 'group' ? t('lots.costs.edit.groupTitle', { count: editing.events.length }) : t('lots.costs.edit.title'))
                : t('lots.costs.group.formTitle', { lot: selectedGroup?.lotKey ?? '', count: selectedGroup?.members.length ?? 0 })}
            </div>
          ) : null}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            <div style={fieldGroupStyle}>
              <label htmlFor="lots-cost-form-category" style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                {t('lots.costs.form.category')}
              </label>
              <select
                id="lots-cost-form-category"
                value={draft.categoryPrefix}
                onChange={(event) => setCategory(event.target.value as ManualCategoryPrefix)}
                disabled={Boolean(editing)}
                title={editing ? t('lots.costs.edit.categoryLocked') : undefined}
                style={inputStyle}
              >
                {MANUAL_CATEGORY_PREFIXES.map((prefix) => (
                  <option key={prefix} value={prefix}>
                    {renderCategoryLabel(prefix)}
                  </option>
                ))}
              </select>
            </div>

            <div style={fieldGroupStyle}>
              <label htmlFor="lots-cost-form-amount" style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                {t('lots.costs.form.amount')}
              </label>
              <input
                id="lots-cost-form-amount"
                value={draft.amountMajor}
                onChange={(event) => setDraft((prev) => ({ ...prev, amountMajor: event.target.value }))}
                inputMode="decimal"
                placeholder="0.00"
                style={errors.amountMajor ? errorInputStyle : inputStyle}
              />
              {errors.amountMajor ? (
                <span style={{ fontSize: 11, color: '#ef4444' }}>
                  {errors.amountMajor === 'amountRequired'
                    ? t('lots.costs.alertAmountRequired')
                    : t('lots.costs.alertAmountInvalid')}
                </span>
              ) : null}
            </div>

            <div style={fieldGroupStyle}>
              <label htmlFor="lots-cost-form-currency" style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                {t('lots.costs.form.currency')}
              </label>
              <input
                id="lots-cost-form-currency"
                value={draft.currency}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, currency: event.target.value.toUpperCase() }))
                }
                maxLength={3}
                style={errors.currency ? errorInputStyle : { ...inputStyle, textTransform: 'uppercase' }}
              />
              {errors.currency ? (
                <span style={{ fontSize: 11, color: '#ef4444' }}>{t('lots.costs.alertCurrencyInvalid')}</span>
              ) : null}
              {showCurrencyMismatchBanner ? (
                <div
                  data-testid="lots-cost-form-currency-mismatch-banner"
                  style={{
                    marginTop: 6,
                    padding: '6px 8px',
                    fontSize: 12,
                    color: '#78350f',
                    background: '#fef3c7',
                    border: '1px solid #fde68a',
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 8
                  }}
                >
                  <span>
                    {t('lots.costs.form.currencyMismatch', {
                      existingCurrencies: existingCurrencies.join(', '),
                      draftCurrency: draftCurrencyCode
                    })}
                  </span>
                  <button
                    type="button"
                    onClick={() => setCurrencyBannerDismissed(true)}
                    style={{
                      background: 'transparent',
                      border: 0,
                      color: '#78350f',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      fontSize: 12,
                      padding: 0
                    }}
                  >
                    {t('lots.costs.form.currencyMismatchDismiss')}
                  </button>
                </div>
              ) : null}
            </div>

            <div style={fieldGroupStyle}>
              <label htmlFor="lots-cost-form-date" style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
                {t('lots.costs.form.date')}
              </label>
              <input
                id="lots-cost-form-date"
                type="date"
                value={draft.recordedAt}
                onChange={(event) => setDraft((prev) => ({ ...prev, recordedAt: event.target.value }))}
                style={errors.recordedAt ? errorInputStyle : inputStyle}
              />
              {errors.recordedAt ? (
                <span style={{ fontSize: 11, color: '#ef4444' }}>{t('lots.costs.alertDateInvalid')}</span>
              ) : null}
            </div>
          </div>

          {groupShares.length > 0 ? (
            <div data-testid="lots-cost-group-split" style={{ display: 'grid', gap: 4, fontSize: 12, color: '#334155' }}>
              <div style={{ fontWeight: 600 }}>
                {groupShares.every((share) => share.count !== null) ? t('lots.costs.group.splitByCount') : t('lots.costs.group.splitEvenly')}
              </div>
              <table style={{ borderCollapse: 'collapse', width: 'fit-content' }}>
                <tbody>
                  {groupShares.map((share) => (
                    <tr key={share.lotId} data-testid={`lots-cost-group-share-${share.lotId}`}>
                      <td style={{ padding: '2px 12px 2px 0' }}>{unitName(share.productionUnitId)}</td>
                      <td style={{ padding: '2px 12px 2px 0', textAlign: 'right' }}>{share.count !== null ? share.count.toLocaleString() : '—'}</td>
                      <td style={{ padding: '2px 12px 2px 0', textAlign: 'right' }}>{share.percent.toFixed(1)} %</td>
                      <td style={{ padding: '2px 0', textAlign: 'right', fontWeight: 600 }}>{formatCostEventDisplayAmount(share.amountCents, draft.currency.trim().toUpperCase() || '—')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <div style={fieldGroupStyle}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{t('lots.costs.form.nature')}</span>
              <div style={toggleGroupStyle}>
                <button
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, expenseNature: 'fixed' }))}
                  style={toggleButtonStyle(draft.expenseNature === 'fixed')}
                >
                  {t('lots.costs.form.natureOption.fixed')}
                </button>
                <button
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, expenseNature: 'variable' }))}
                  style={toggleButtonStyle(draft.expenseNature === 'variable')}
                >
                  {t('lots.costs.form.natureOption.variable')}
                </button>
              </div>
            </div>

            <div style={fieldGroupStyle}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>{t('lots.costs.form.classification')}</span>
              <div style={toggleGroupStyle}>
                <button
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, costClassification: 'direct' }))}
                  style={toggleButtonStyle(draft.costClassification === 'direct')}
                >
                  {t('lots.costs.form.classificationOption.direct')}
                </button>
                <button
                  type="button"
                  onClick={() => setDraft((prev) => ({ ...prev, costClassification: 'indirect' }))}
                  style={toggleButtonStyle(draft.costClassification === 'indirect')}
                >
                  {t('lots.costs.form.classificationOption.indirect')}
                </button>
              </div>
            </div>
          </div>

          <div style={fieldGroupStyle}>
            <label htmlFor="lots-cost-form-notes" style={{ fontSize: 12, fontWeight: 600, color: '#334155' }}>
              {t('lots.costs.form.notes')}
            </label>
            <textarea
              id="lots-cost-form-notes"
              value={draft.notes}
              onChange={(event) => setDraft((prev) => ({ ...prev, notes: event.target.value }))}
              rows={2}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={closeForm} style={secondaryButtonStyle} disabled={submitting}>
              {t('lots.costs.form.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void handleSubmit()}
              style={primaryButtonStyle}
              disabled={submitting}
              data-testid="lots-cost-form-save"
            >
              {submitting ? t('lots.costs.form.saving') : t('lots.costs.form.save')}
            </button>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div style={{ fontSize: 13, color: '#64748b' }}>{t('lots.costs.loading')}</div>
      ) : loadError ? (
        <div style={{ fontSize: 13, color: '#b91c1c' }}>{loadError}</div>
      ) : events.length === 0 ? (
        <div style={{ fontSize: 13, color: '#64748b' }}>
          {isFacilityScope ? t('costs.facilityWide.empty') : selectedLot || selectedGroup ? t('lots.costs.empty') : t('lots.costs.noLotsInScope')}
        </div>
      ) : (
        <ul
          data-testid="lots-cost-list"
          style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}
        >
          {(selectedGroup ? groupCostEventsForList(events) : events.map((event) => ({ kind: 'single' as const, event }))).map((item) => {
            if (item.kind === 'group') {
              const first = item.events[0]
              const isPendingGroupDelete = pendingDeleteId === item.groupId
              return (
                <li
                  key={item.groupId}
                  data-testid={`lots-cost-group-${item.groupId}`}
                  style={{ display: 'grid', gridTemplateColumns: 'minmax(60px, auto) 1fr auto', gap: 8, padding: '8px 10px', border: '1px solid #c7d2fe', borderRadius: 8, alignItems: 'center', backgroundColor: '#f8fafc' }}
                >
                  <span style={{ fontFamily: 'monospace', fontSize: 12, padding: '2px 6px', backgroundColor: '#eef2ff', color: '#3730a3', borderRadius: 4, textAlign: 'center' }}>
                    {renderCategoryLabel(first.categoryPrefix)}
                  </span>
                  <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                      {formatCostEventDisplayAmount(item.totalCents, first.currency)}
                      <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>
                        · {t('lots.costs.group.splitInto', { count: item.events.length })}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                      {first.recordedAt.slice(0, 10)} · {item.events.map((event) => `${unitName(event.productionUnitId)} ${formatCostEventDisplayAmount(event.amountCents, event.currency)}`).join(' · ')}
                    </div>
                    {first.notes ? <div style={{ fontSize: 12, color: '#475569' }}>{first.notes}</div> : null}
                  </div>
                  {canEdit ? (
                    isPendingGroupDelete ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button type="button" onClick={() => setPendingDeleteId(null)} style={secondaryButtonStyle}>{t('lots.costs.form.cancel')}</button>
                        <button type="button" onClick={() => void handleDeleteGroup(item.events)} style={dangerButtonStyle}>{t('lots.costs.confirmDelete')}</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button type="button" data-testid={`lots-cost-group-edit-${item.groupId}`} onClick={() => openEdit({ kind: 'group', groupId: item.groupId, events: item.events })} style={editButtonStyle}>{t('lots.costs.edit.button')}</button>
                        <button type="button" onClick={() => setPendingDeleteId(item.groupId)} style={dangerButtonStyle}>{t('lots.costs.deleteButton')}</button>
                      </div>
                    )
                  ) : null}
                </li>
              )
            }
            const entry = item.event
            const isAutoDerived = entry.sourceEventType === 'feedLog'
            const isPendingDelete = pendingDeleteId === entry.id
            // A share of a whole-lot cost is edited from "todo el lote".
            const groupShareOfTotal = !selectedGroup && readCostGroupId(entry)
              ? (entry.metadata as Record<string, unknown> | undefined)?.costGroupTotalCents
              : undefined
            const isGroupShare = !selectedGroup && Boolean(readCostGroupId(entry))
            // Feed / treatment costs follow their source record; edit that record instead.
            const canEditEntry = canEdit && !entry.sourceEventType && !isGroupShare
            return (
              <li
                key={entry.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(60px, auto) 1fr auto',
                  gap: 8,
                  padding: '8px 10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  alignItems: 'center'
                }}
              >
                <span
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 12,
                    padding: '2px 6px',
                    backgroundColor: '#eef2ff',
                    color: '#3730a3',
                    borderRadius: 4,
                    textAlign: 'center'
                  }}
                >
                  {entry.categoryCode}
                </span>
                <div style={{ display: 'grid', gap: 2, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>
                    {formatCostEventDisplayAmount(entry.amountCents, entry.currency)}
                    <span style={{ color: '#64748b', fontWeight: 400, marginLeft: 8 }}>
                      · {renderCategoryLabel(entry.categoryPrefix)}
                      {selectedGroup ? ` · ${unitName(entry.productionUnitId)}` : ''}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>
                    {entry.recordedAt.slice(0, 10)} · {renderNatureLabel(entry.expenseNature)} ·{' '}
                    {renderClassificationLabel(entry.costClassification)}
                    {isAutoDerived ? ` · ${t('lots.costs.autoDerivedNote')}` : ''}
                  </div>
                  {isGroupShare ? (
                    <div data-testid={`lots-cost-group-share-note-${entry.id}`} style={{ fontSize: 11, color: '#4338ca' }}>
                      {t('lots.costs.group.shareNote', {
                        total: typeof groupShareOfTotal === 'number' ? formatCostEventDisplayAmount(groupShareOfTotal, entry.currency) : '—'
                      })}
                    </div>
                  ) : null}
                  {entry.notes ? (
                    <div style={{ fontSize: 12, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {entry.notes}
                    </div>
                  ) : null}
                </div>
                {canEdit && !isGroupShare ? (
                  isPendingDelete ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(null)}
                        style={secondaryButtonStyle}
                      >
                        {t('lots.costs.form.cancel')}
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(entry.id)}
                        style={dangerButtonStyle}
                      >
                        {t('lots.costs.confirmDelete')}
                      </button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4 }}>
                      {canEditEntry ? (
                        <button
                          type="button"
                          data-testid={`lots-cost-edit-${entry.id}`}
                          onClick={() => openEdit({ kind: 'single', event: entry })}
                          style={editButtonStyle}
                        >
                          {t('lots.costs.edit.button')}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setPendingDeleteId(entry.id)}
                        style={dangerButtonStyle}
                        title={t('lots.costs.deleteHint')}
                      >
                        {t('lots.costs.deleteButton')}
                      </button>
                    </div>
                  )
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </section>
  )
}
